import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import {
  ArrowLeft,
  Building2,
  Link2,
  Loader2,
  Trash2,
  User
} from 'lucide-react';

const labelsTipoAcesso = {
  administrador: 'Administrador',
  sindico: 'Síndico',
  subsindico: 'Subsíndico',
  porteiro: 'Porteiro',
  morador: 'Morador',
  funcionario: 'Funcionário'
};

const VincularUsuario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { condominios: condominiosContexto } = useCondominio();

  const [usuario, setUsuario] = useState(null);
  const [vinculos, setVinculos] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [form, setForm] = useState({
    condominio_id: '',
    bloco: '',
    apartamento_id: '',
    tipo_acesso: 'morador'
  });

  const carregarVinculos = async () => {
    const response = await api.get(`/usuarios/${id}/vinculos`);
    setUsuario(response.data?.data?.usuario || null);
    setVinculos(response.data?.data?.vinculos || []);
  };

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        setErro('');
        await carregarVinculos();

        const condRes = await api.get('/condominios', { params: { limit: 1000 } });
        const lista =
          condRes.data?.data?.condominios ||
          condRes.data?.condominios ||
          condominiosContexto ||
          [];
        setCondominios(Array.isArray(lista) ? lista : []);
      } catch (error) {
        setErro(error.response?.data?.message || 'Erro ao carregar vínculos do usuário.');
      } finally {
        setLoading(false);
      }
    };

    if (id) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const carregarApartamentos = async () => {
      if (!form.condominio_id) {
        setApartamentos([]);
        return;
      }
      try {
        const response = await api.get('/apartamentos', {
          params: { condominio_id: form.condominio_id, limit: 1000 }
        });
        const lista = response.data?.data?.apartamentos || response.data?.apartamentos || [];
        setApartamentos(Array.isArray(lista) ? lista : []);
      } catch (error) {
        console.error('Erro ao carregar unidades:', error);
        setApartamentos([]);
      }
    };

    carregarApartamentos();
  }, [form.condominio_id]);

  const handleVincular = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso('');

    if (!form.condominio_id) {
      setErro('Selecione um condomínio.');
      return;
    }

    try {
      setSaving(true);
      await api.post('/usuarios/vincular', {
        usuario_id: Number(id),
        condominio_id: Number(form.condominio_id),
        apartamento_id: form.apartamento_id ? Number(form.apartamento_id) : null,
        tipo_acesso: form.tipo_acesso
      });
      setSucesso('Usuário vinculado com sucesso.');
      setForm((prev) => ({ ...prev, apartamento_id: '' }));
      await carregarVinculos();
    } catch (error) {
      setErro(error.response?.data?.message || 'Erro ao vincular usuário.');
    } finally {
      setSaving(false);
    }
  };

  const handleDesvincular = async (vinculo) => {
    const nomeCondo = vinculo.condominio?.condominio_nome || 'este condomínio';
    if (!window.confirm(`Remover o vínculo com ${nomeCondo}? O usuário não será excluído.`)) {
      return;
    }

    setErro('');
    setSucesso('');
    try {
      setUnlinkingId(vinculo.usuario_condominio_id);
      await api.delete(`/usuarios/vinculos/${vinculo.usuario_condominio_id}`);
      setSucesso('Vínculo removido. O usuário continua cadastrado.');
      await carregarVinculos();
    } catch (error) {
      setErro(error.response?.data?.message || 'Erro ao desvincular usuário.');
    } finally {
      setUnlinkingId(null);
    }
  };

  const unidadeLabel = (vinculo) => {
    const apt = vinculo.apartamento;
    if (!apt) return 'Condomínio (sem unidade)';
    const bloco = apt.apartamento_bloco ? `${apt.apartamento_bloco} - ` : '';
    return `${bloco}${apt.apartamento_numero || ''}`.trim() || 'Unidade';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        Carregando vínculos...
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/usuarios')}
            className="text-gray-600 hover:text-gray-900"
            title="Voltar"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <Link2 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vínculos do usuário</h1>
            <p className="text-sm text-gray-500">Vincule ou remova o acesso deste usuário a condomínios</p>
          </div>
        </div>
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mb-4">
          {erro}
        </div>
      )}
      {sucesso && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 mb-4">
          {sucesso}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">{usuario?.usuario_nome || 'Usuário'}</h2>
            <p className="text-sm text-gray-600">{usuario?.usuario_email || '—'}</p>
            {usuario?.usuario_cpf && (
              <p className="text-sm text-gray-500">CPF: {usuario.usuario_cpf}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Novo vínculo</h2>
        <form onSubmit={handleVincular} className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Condomínio</label>
            <select
              value={form.condominio_id}
              onChange={(e) => setForm((prev) => ({ ...prev, condominio_id: e.target.value, bloco: '', apartamento_id: '' }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            >
              <option value="">Selecione</option>
              {condominios.map((c) => (
                <option key={c.condominio_id} value={c.condominio_id}>
                  {c.condominio_nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bloco</label>
            <select
              value={form.bloco}
              onChange={(e) => setForm((prev) => ({ ...prev, bloco: e.target.value, apartamento_id: '' }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              disabled={!form.condominio_id}
            >
              <option value="">Todos</option>
              {[...new Set(apartamentos.map((apt) => apt.apartamento_bloco).filter(Boolean))].sort().map((bloco) => (
                <option key={bloco} value={bloco}>{bloco}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidade (opcional)</label>
            <select
              value={form.apartamento_id}
              onChange={(e) => setForm((prev) => ({ ...prev, apartamento_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              disabled={!form.condominio_id}
            >
              <option value="">Sem unidade específica</option>
              {apartamentos
                .filter((apt) => !form.bloco || String(apt.apartamento_bloco || '') === form.bloco)
                .map((apt) => (
                <option key={apt.apartamento_id} value={apt.apartamento_id}>
                  {apt.apartamento_bloco ? `${apt.apartamento_bloco} - ` : ''}
                  {apt.apartamento_numero}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de acesso</label>
            <select
              value={form.tipo_acesso}
              onChange={(e) => setForm((prev) => ({ ...prev, tipo_acesso: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              {Object.entries(labelsTipoAcesso).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Vincular
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Vínculos atuais</h2>
          <p className="text-sm text-gray-500">Remover o vínculo tira o acesso ao condomínio, sem apagar o cadastro do usuário.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condomínio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vinculos.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    <Building2 className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    Este usuário não está vinculado a nenhum condomínio.
                  </td>
                </tr>
              ) : (
                vinculos.map((vinculo) => (
                  <tr key={vinculo.usuario_condominio_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {vinculo.condominio?.condominio_nome || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{unidadeLabel(vinculo)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {labelsTipoAcesso[vinculo.tipo_acesso] || vinculo.tipo_acesso}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        vinculo.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {vinculo.status || 'ativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleDesvincular(vinculo)}
                        disabled={unlinkingId === vinculo.usuario_condominio_id}
                        className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 disabled:opacity-50"
                        title="Desvincular"
                      >
                        {unlinkingId === vinculo.usuario_condominio_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Desvincular
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VincularUsuario;
